import { useEffect, useRef, useState } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  AnimatePresence,
} from "framer-motion";
import { RotateCcw, Bot, User } from "lucide-react";
import axios from "axios";
import { useLang, STR } from "@/i18n";
import { sfx } from "@/sound";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SIZE = 6;
const TYPES = 6;
const MOVES_EACH = 8;

const CANDIES = [
  { color: "#fbbf24", shape: "star" },
  { color: "#2dd4bf", shape: "square" },
  { color: "#34d399", shape: "triangle" },
  { color: "#a78bfa", shape: "diamond" },
  { color: "#fb923c", shape: "heart" },
  { color: "#f43f5e", shape: "dot" },
];

const CandyIcon = ({ type }) => {
  const { color, shape } = CANDIES[type];
  return (
    <svg viewBox="0 0 40 40" className="h-full w-full" aria-hidden="true">
      <circle cx="20" cy="20" r="17" fill="none" stroke={color} strokeWidth="2" opacity="0.95" />
      {shape === "star" && (
        <polygon
          points="20,10.5 23.3,17.3 30.6,17.9 25.3,22.9 27,30.1 20,26 13,30.1 14.7,22.9 9.4,17.9 16.7,17.3"
          fill={color}
        />
      )}
      {shape === "square" && <rect x="13" y="13" width="14" height="14" rx="2.5" fill={color} />}
      {shape === "triangle" && <polygon points="20,12 28.5,27 11.5,27" fill={color} strokeLinejoin="round" />}
      {shape === "diamond" && <polygon points="20,11 29,20 20,29 11,20" fill={color} />}
      {shape === "heart" && (
        <path
          d="M20 27.5 C14.5 23 11.5 19.6 11.5 16.6 C11.5 13.9 13.6 12.2 15.8 12.2 C17.6 12.2 19.1 13.2 20 14.6 C20.9 13.2 22.4 12.2 24.2 12.2 C26.4 12.2 28.5 13.9 28.5 16.6 C28.5 19.6 25.5 23 20 27.5 Z"
          fill={color}
        />
      )}
      {shape === "dot" && <circle cx="20" cy="20" r="7" fill={color} />}
    </svg>
  );
};

const idx = (r, c) => r * SIZE + c;
const rc = (i) => [Math.floor(i / SIZE), i % SIZE];

function randomBoard() {
  const b = new Array(SIZE * SIZE).fill(0);
  for (let r = 0; r < SIZE; r++)
    for (let c = 0; c < SIZE; c++) {
      let v;
      do {
        v = Math.floor(Math.random() * TYPES);
      } while (
        (c >= 2 && b[idx(r, c - 1)] === v && b[idx(r, c - 2)] === v) ||
        (r >= 2 && b[idx(r - 1, c)] === v && b[idx(r - 2, c)] === v)
      );
      b[idx(r, c)] = v;
    }
  return b;
}

function findMatches(b) {
  const hit = new Set();
  for (let r = 0; r < SIZE; r++)
    for (let c = 0; c < SIZE - 2; c++) {
      const v = b[idx(r, c)];
      if (v >= 0 && v === b[idx(r, c + 1)] && v === b[idx(r, c + 2)]) {
        hit.add(idx(r, c));
        hit.add(idx(r, c + 1));
        hit.add(idx(r, c + 2));
        let cc = c + 3;
        while (cc < SIZE && b[idx(r, cc)] === v) {
          hit.add(idx(r, cc));
          cc++;
        }
      }
    }
  for (let c = 0; c < SIZE; c++)
    for (let r = 0; r < SIZE - 2; r++) {
      const v = b[idx(r, c)];
      if (v >= 0 && v === b[idx(r + 1, c)] && v === b[idx(r + 2, c)]) {
        hit.add(idx(r, c));
        hit.add(idx(r + 1, c));
        hit.add(idx(r + 2, c));
        let rr = r + 3;
        while (rr < SIZE && b[idx(rr, c)] === v) {
          hit.add(idx(rr, c));
          rr++;
        }
      }
    }
  return hit;
}

function applyGravity(b, removed) {
  const nb = b.slice();
  removed.forEach((i) => (nb[i] = -1));
  for (let c = 0; c < SIZE; c++) {
    let write = SIZE - 1;
    for (let r = SIZE - 1; r >= 0; r--) {
      const v = nb[idx(r, c)];
      if (v >= 0) {
        nb[idx(write, c)] = v;
        if (write !== r) nb[idx(r, c)] = -1;
        write--;
      }
    }
    for (let r = write; r >= 0; r--) nb[idx(r, c)] = Math.floor(Math.random() * TYPES);
  }
  return nb;
}

function validMoves(b) {
  const moves = [];
  const trySwap = (i, j) => {
    const nb = b.slice();
    [nb[i], nb[j]] = [nb[j], nb[i]];
    const gain = findMatches(nb).size;
    if (gain > 0) moves.push({ i, j, gain });
  };
  for (let r = 0; r < SIZE; r++)
    for (let c = 0; c < SIZE; c++) {
      if (c < SIZE - 1) trySwap(idx(r, c), idx(r, c + 1));
      if (r < SIZE - 1) trySwap(idx(r, c), idx(r + 1, c));
    }
  return moves;
}

function ensurePlayable(b) {
  let board = b;
  let guard = 0;
  while (validMoves(board).length === 0 && guard < 50) {
    board = randomBoard();
    guard++;
  }
  return board;
}

export default function CandyMatch() {
  const { lang } = useLang();
  const t = STR[lang];
  const [board, setBoard] = useState(() => ensurePlayable(randomBoard()));
  const [selected, setSelected] = useState(null);
  const [phase, setPhase] = useState("player"); // player | ai | resolving | over
  const [popped, setPopped] = useState(new Set());
  const [notice, setNotice] = useState(null);
  const [score, setScore] = useState({ you: 0, ai: 0 });
  const [moves, setMoves] = useState({ you: MOVES_EACH, ai: MOVES_EACH });
  const token = useRef(0);
  const timers = useRef([]);

  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(my, [0, 1], [12, -12]), { stiffness: 120, damping: 16 });
  const rotateY = useSpring(useTransform(mx, [0, 1], [-14, 14]), { stiffness: 120, damping: 16 });

  const wait = (ms) =>
    new Promise((res) => {
      timers.current.push(setTimeout(res, ms));
    });

  useEffect(
    () => () => {
      token.current++;
      timers.current.forEach(clearTimeout);
    },
    []
  );

  const resolveBoard = async (b, myToken) => {
    let current = b;
    let total = 0;
    let cascade = 0;
    for (;;) {
      const m = findMatches(current);
      if (m.size === 0) break;
      total += m.size * 10 * (cascade + 1);
      cascade++;
      setPopped(m);
      sfx.move();
      await wait(330);
      if (token.current !== myToken) return null;
      current = applyGravity(current, m);
      setBoard(current);
      setPopped(new Set());
      await wait(210);
      if (token.current !== myToken) return null;
    }
    return { board: current, total };
  };

  const endGame = (finalScore) => {
    setPhase("over");
    const res = finalScore.you > finalScore.ai ? "you" : finalScore.you < finalScore.ai ? "ai" : "draw";
    if (res === "you") sfx.win();
    else if (res === "ai") sfx.lose();
    else sfx.draw();
    axios.post(`${API}/score`, { result: res === "draw" ? "draw" : res }).catch(() => {});
  };

  const aiTurn = async (b, myToken, curScore, curMoves) => {
    setPhase("ai");
    await wait(950);
    if (token.current !== myToken) return;
    let opts = validMoves(b);
    let work = b;
    if (opts.length === 0) {
      work = randomBoard();
      setBoard(work);
      opts = validMoves(work);
    }
    const best = opts.reduce((a, o) => (o.gain > a.gain ? o : a), opts[0]);
    const nb = work.slice();
    [nb[best.i], nb[best.j]] = [nb[best.j], nb[best.i]];
    setBoard(nb);
    sfx.move();
    const out = await resolveBoard(nb, myToken);
    if (!out) return;
    const next = ensurePlayable(out.board);
    if (next !== out.board) setBoard(next);
    const ns = { ...curScore, ai: curScore.ai + out.total };
    const nm = { ...curMoves, ai: curMoves.ai - 1 };
    setScore(ns);
    setMoves(nm);
    if (nm.you === 0 && nm.ai === 0) endGame(ns);
    else setPhase("player");
  };

  const doPlayerTurn = async (nb, myToken) => {
    setPhase("resolving");
    setBoard(nb);
    sfx.move();
    const out = await resolveBoard(nb, myToken);
    if (!out) return;
    const next = ensurePlayable(out.board);
    if (next !== out.board) setBoard(next);
    const ns = { ...score, you: score.you + out.total };
    const nm = { ...moves, you: moves.you - 1 };
    setScore(ns);
    setMoves(nm);
    if (nm.you === 0 && nm.ai === 0) endGame(ns);
    else aiTurn(next, myToken, ns, nm);
  };

  const onCell = (i) => {
    if (phase !== "player" || moves.you <= 0) return;
    if (selected === null) {
      setSelected(i);
      sfx.move();
      return;
    }
    if (selected === i) {
      setSelected(null);
      return;
    }
    const [r1, c1] = rc(selected);
    const [r2, c2] = rc(i);
    if (Math.abs(r1 - r2) + Math.abs(c1 - c2) !== 1) {
      setSelected(i);
      sfx.move();
      return;
    }
    const nb = board.slice();
    [nb[selected], nb[i]] = [nb[i], nb[selected]];
    setSelected(null);
    if (findMatches(nb).size === 0) {
      sfx.draw();
      setNotice(t.candy_invalid);
      timers.current.push(setTimeout(() => setNotice(null), 1400));
      return;
    }
    doPlayerTurn(nb, ++token.current);
  };

  const reset = () => {
    token.current++;
    timers.current.forEach(clearTimeout);
    timers.current = [];
    setBoard(ensurePlayable(randomBoard()));
    setSelected(null);
    setPopped(new Set());
    setNotice(null);
    setScore({ you: 0, ai: 0 });
    setMoves({ you: MOVES_EACH, ai: MOVES_EACH });
    setPhase("player");
  };

  const outcome =
    phase === "over" ? (score.you > score.ai ? "win" : score.you < score.ai ? "lose" : "draw") : null;

  const status =
    notice != null
      ? { text: notice, cls: "text-rose-300" }
      : phase === "player"
        ? { text: t.candy_turn_you, cls: "text-sky-300" }
        : phase === "over"
          ? {
              text: outcome === "win" ? t.st_win : outcome === "draw" ? t.st_draw : t.st_lose,
              cls: outcome === "win" ? "text-sky-300" : outcome === "draw" ? "text-slate-300" : "text-amber-300",
            }
          : { text: t.candy_turn_ai, cls: "text-amber-300" };

  return (
    <div
      data-testid="candy-game"
      className="relative"
      style={{ perspective: 1200 }}
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width);
        my.set((e.clientY - r.top) / r.height);
      }}
      onMouseLeave={() => {
        mx.set(0.5);
        my.set(0.5);
      }}
    >
      <div className="absolute -inset-10 bg-[radial-gradient(circle_at_50%_40%,rgba(244,114,182,0.16),rgba(251,191,36,0.1)_45%,transparent_70%)] blur-2xl pointer-events-none" />

      <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }} className="relative">
      <div className="rounded-[2rem] border border-white/10 bg-[#0c1122]/90 backdrop-blur-xl p-5 sm:p-7 shadow-[0_40px_120px_-20px_rgba(244,114,182,0.28)]">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="grid place-items-center w-8 h-8 rounded-lg bg-sky-400/15 border border-sky-400/30">
              <User className="w-4 h-4 text-sky-300" />
            </span>
            <div>
              <p className="text-xs font-bold text-white leading-none">{t.you}</p>
              <p data-testid="candy-score-you" className="mono text-[10px] text-sky-300 mt-1">
                {score.you} pts · {moves.you} {t.candy_moves}
              </p>
            </div>
          </div>
          <span className="mono text-[10px] uppercase tracking-[0.3em] text-slate-500">vs</span>
          <div className="flex items-center gap-2">
            <div className="text-right">
              <p className="text-xs font-bold text-white leading-none">DUELO IA</p>
              <p data-testid="candy-score-ai" className="mono text-[10px] text-amber-300 mt-1">
                {score.ai} pts · {moves.ai} {t.candy_moves}
              </p>
            </div>
            <span className="grid place-items-center w-8 h-8 rounded-lg bg-amber-400/15 border border-amber-400/30">
              <Bot className="w-4 h-4 text-amber-300" />
            </span>
          </div>
        </div>

        <div className="relative">
          <div className="grid grid-cols-6 gap-1.5 sm:gap-2">
            {board.map((v, i) => (
              <button
                key={i}
                type="button"
                data-testid={`candy-cell-${i}`}
                data-type={v}
                onClick={() => onCell(i)}
                disabled={phase !== "player"}
                className={`relative aspect-square rounded-full p-0.5 transition duration-150 ${
                  selected === i
                    ? "scale-110 drop-shadow-[0_0_10px_rgba(255,255,255,0.45)]"
                    : "hover:scale-105"
                } ${phase === "player" ? "cursor-pointer" : "cursor-default"}`}
              >
                {v >= 0 && (
                  <span
                    className={`block h-full w-full transition-all duration-200 ${
                      popped.has(i) ? "scale-0 opacity-0" : "scale-100 opacity-100"
                    }`}
                  >
                    <CandyIcon type={v} />
                  </span>
                )}
              </button>
            ))}
          </div>

          <AnimatePresence>
            {phase === "over" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 rounded-2xl bg-[#070b18]/85 backdrop-blur-sm"
              >
                <motion.p
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 260, damping: 16 }}
                  className={`font-display font-black text-2xl text-center ${
                    outcome === "win" ? "text-sky-300" : outcome === "draw" ? "text-slate-300" : "text-amber-300"
                  }`}
                >
                  {outcome === "win" ? t.st_win : outcome === "draw" ? t.st_draw : t.st_lose}
                </motion.p>
                <p className="mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
                  {score.you} × {score.ai} pts
                </p>
                <button
                  type="button"
                  onClick={reset}
                  data-testid="candy-play-again-btn"
                  className="rounded-full bg-amber-300 px-5 py-2.5 text-xs font-black uppercase tracking-[0.12em] text-[#130b25] transition hover:bg-amber-200 active:scale-95"
                >
                  {t.candy_again}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <p data-testid="candy-status" className={`mono text-[11px] uppercase tracking-[0.15em] ${status.cls}`}>
            {status.text}
          </p>
          <button
            onClick={reset}
            data-testid="candy-reset-btn"
            className="grid place-items-center w-9 h-9 rounded-full border border-white/10 text-slate-400 hover:text-amber-300 hover:border-amber-400/40 hover:rotate-[-120deg] transition-all duration-300"
            aria-label="Reiniciar partida"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </div>
      </motion.div>
    </div>
  );
}
